import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, Phone, Mail, Facebook, Youtube, ChevronRight, 
  HelpCircle, MessageCircle, Code2 
} from 'lucide-react';
import schoollogo from '../assets/logo.png';

const Footer = () => {
  const socialLinks = [
    { icon: Facebook, url: "https://www.facebook.com/share/g/18Ao15ZRRG/", label: "Facebook" },
    { icon: Youtube, url: "https://youtube.com/@kafrelsheikhstem4180?si=jUwBiuDW4gBD7ScD", label: "Youtube" },
  ];

  // Official WhatsApp Contact
  const whatsappNumber = "201062063658"; // Replace with the official WhatsApp number
  const whatsappMessage = encodeURIComponent("");

  return (
    <footer className="w-full font-sans bg-white  border-gray-100">
      {/* --- UPPER SECTION: DIRECTORY --- */}
      <div className="max-w-7xl mx-auto py-20 px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
        
        {/* Column 1: Institutional Identity */}
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <img src={schoollogo} alt="School Logo" width="56" height="56" className="h-14 w-14 object-contain" />
            <div className="flex flex-col">
              <span className="font-black text-xl text-[#1e3a8a] tracking-tighter uppercase leading-none">Kafr Elsheikh </span>
              <span className="text-[10px] font-bold text-[#8B0000] uppercase tracking-widest mt-1"> STEM School</span>
            </div>
          </div>
          <p className="text-gray-500 text-sm leading-relaxed">
            Leading the path in advanced scientific research and engineering excellence under the Egyptian Ministry of Education.
          </p>
        </div>

        {/* Column 2: Navigation */}
        <div className="space-y-6">
          <h3 className="text-[#1e3a8a] font-black text-xs uppercase tracking-[0.3em] border-b border-gray-100 pb-2">Information</h3>
          <ul className="space-y-3">
            {['Home', 'Staff', 'Achievements', 'Campus', 'Contact'].map((item) => (
              <li key={item}>
                <Link 
                  to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                  className="group flex items-center gap-2 text-gray-400 hover:text-[#8B0000] transition-colors text-xs font-bold uppercase tracking-widest"
                >
                  <ChevronRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 3: Contact */}
        <div className="space-y-6">
          <h3 className="text-[#1e3a8a] font-black text-xs uppercase tracking-[0.3em] border-b border-gray-100 pb-2">Direct Contact</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-gray-500">
              <MapPin size={16} className="text-[#8B0000] shrink-0" />
              <span className="text-xs font-medium">Kafr Elsheikh, Egypt</span>
            </div>
            <div className="flex items-center gap-4 text-gray-500">
              <Phone size={16} className="text-[#8B0000] shrink-0" />
              <span className="text-xs font-medium">+20 10 09640334</span>
            </div>
            <div className="flex items-center gap-4 text-gray-500">
              <Mail size={16} className="text-[#8B0000] shrink-0" />
              <span className="text-xs font-medium truncate italic">maged.khalil@stemksheikh.moe.edu.eg</span>
            </div>
          </div>
        </div>

        {/* Column 4: Official Bodies */}
        <div className="space-y-6">
          <h3 className="text-[#1e3a8a] font-black text-xs uppercase tracking-[0.3em] border-b border-gray-100 pb-2">Affiliations</h3>
          <div className="flex flex-col gap-2">
            {['Ministry of Education', 'STEM Unit Egypt'].map((text) => (
              <div key={text} className="py-3 px-4 bg-gray-50 text-[9px] font-black text-gray-400 uppercase tracking-widest text-center rounded-lg border border-gray-100">
                {text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- SUPPORT & AUDIT BANNER --- */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="bg-[#f8f9fa] border border-gray-200/80 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          
          <div className="flex items-start gap-4 text-left">
            <div className="w-10 h-10 rounded-lg bg-[#1e3a8a]/10 text-[#1e3a8a] flex items-center justify-center shrink-0 mt-0.5">
              <HelpCircle size={20} />
            </div>
            <div className="space-y-1">
              <h4 className="text-sm font-black text-[#1e3a8a] uppercase tracking-wider">
                Support & Content Verification
              </h4>
              <p className="text-xs text-gray-500 leading-relaxed max-w-xl">
                Notice a technical error or incorrect information on the school website? Reach out directly via WhatsApp to help us keep our portal updated and accurate.
              </p>
            </div>
          </div>

          <a 
            href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 bg-[#8B0000] hover:bg-[#660000] text-white text-xs font-bold uppercase tracking-widest px-6 py-3.5 rounded-xl transition-all duration-200 shrink-0 shadow-sm active:scale-95 no-underline"
          >
            <MessageCircle size={15} />
            <span>Report via WhatsApp</span>
          </a>

        </div>
      </div>

      {/* --- BOTTOM BAR: OFFICIAL & CREDITS --- */}
      <div className="bg-[#0f172a] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-6">
          
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <img src={schoollogo} alt="KFS STEM" width="32" height="32" className="h-8 w-8 brightness-0 invert opacity-80" />
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">
                © {new Date().getFullYear()} <span className="text-white opacity-90">Kafr Elsheikh STEM School.</span>
              </p>
            </div>

            {/* Clean Developer Credit Pill */}
            <a
            href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold tracking-widest uppercase">
              <Code2 size={12} className="text-[#ffffff]" />
              <span className="text-slate-400">Built by</span>
              <span className="text-white ">Youssef Musad</span>
            </a>
          </div>
          
          {/* Social Icons */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social, i) => (
              <a 
                aria-label={`Join our ${social.label} Community`}
                key={i} 
                href={social.url} 
                target="_blank" 
                rel="noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-[#8B0000] hover:border-[#8B0000] transition-all duration-300"
              >
                <social.icon size={16} />
              </a>
            ))}
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;